'use client'

import { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react'
import { z } from 'zod'

import { clearOnboardingCookie } from '@/app/actions/onboarding-cookie.actions'
import { useAppContext } from '@/context/AppContext'
import { useCustomer } from '@/context/customer/CustomerContext'
import { useZodForm } from '@/hooks/useZodForm'

import { formSchema } from './Onboarding.schema'
import { ApplianceFormValidationErrors } from './Onboarding.types'
import { getInitialState, getApplianceFormIsValid } from './Onboarding.utils'
import FormioWrapper from '../Forms/FormioWrapper'

import type { FORMIO_FIELDS, FormErrors, OnBoardingFormDataType, MutationResult, InputError, UserError } from './Onboarding.schema'
import type { OnboardingState, ProductForm, ApplianceFormValidationResult } from './Onboarding.types'
import type { OnboardingCookie } from '@/types/cookies'
import type { CountryCode } from '@/types/shopify/admin/api-types'
import type { Webform } from '@formio/js'
import type { Dispatch, SetStateAction } from 'react'

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding must be used within an OnboardingProvider')

  return ctx
}

type OnboardingContextValue = {
  openAtStep: (step: number) => void
  nextStep: () => void
  previousStep: () => void
  onboardingFormData: OnBoardingFormDataType
  setOnboardingFormData: (onboardingFormData: (prev: OnBoardingFormDataType) => OnBoardingFormDataType) => void
  onboardingFormErrors: FormErrors
  handleOnboardingFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  setOnboardingFormErrors: (errors: FormErrors) => void
  clearOnboardingFormError: (field: keyof FormErrors) => void
  clearAllOnboardingFormErrors: () => void
  isOnboardingFormPending: boolean
  isOnboardingFormError: boolean
  isOnboardingFormButtonDisabled: boolean
  handleOnboardingFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  displayAddress: string
  setDisplayAddress: (displayAddress: string) => void
  customerState: OnboardingState
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  currentStep: number
  productForm: ProductForm
  setProductForm: Dispatch<SetStateAction<ProductForm>>
  initialAppliances?: Record<string, boolean | string>
  fromOnboardingCookie: boolean
  isBusinessForm: (data: OnBoardingFormDataType) => data is Extract<OnBoardingFormDataType, { isBusiness: true }>
  ApplianceForm: React.ReactNode
  submitApplianceForm: () => void
  validateApplianceForm: () => ApplianceFormValidationResult
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined)

export const OnboardingProvider = ({ children, onboardingCookie }: { children: React.ReactNode; onboardingCookie?: OnboardingCookie }) => {
  const { session } = useAppContext()
  const { customer, mutations, handleBuyerIdentityChange } = useCustomer()

  const {
    customerState: initialCustomerState,
    onboardingForm,
    displayAddress: initialDisplayAddress,
    productForm: initialProductForm,
    initialAppliances,
  } = getInitialState({ customer, onboardingCookie })

  const [customerState, setCustomerState] = useState<OnboardingState>(initialCustomerState)
  const [productForm, setProductForm] = useState<ProductForm>(initialProductForm)
  const [isOpen, setIsOpen] = useState(customerState === 'ONBOARDING' ? session.isLoggedIn : false)
  const [currentStep, setCurrentStep] = useState(1)

  const [displayAddress, setDisplayAddress] = useState(initialDisplayAddress) // This is required for Onboarding Cookie, sets defaultValue against PlacesAutocomplete
  const { formData: onboardingFormData, setFormData: setOnboardingFormData, handleOnChange: handleOnboardingFormChange } = useZodForm(formSchema, onboardingForm)
  const [onboardingFormErrors, setOnboardingFormErrors] = useState<FormErrors>({})

  const FORM_IO_PROJECT_URL = process.env.NEXT_PUBLIC_FORM_IO_PROJECT_URL
  const FORM_URL = process.env.NEXT_PUBLIC_FORM_IO_LPG_APPLIANCE
  const formUrl = FORM_IO_PROJECT_URL + '/' + FORM_URL
  const [formInstance, setFormInstance] = useState<Webform>()
  const [applianceFormIsValid, setApplianceFormIsValid] = useState<ApplianceFormValidationResult>({
    isValid: true,
    error: null,
  })

  const initialAppliancesRef = useRef(initialAppliances)
  if (initialAppliances !== undefined) {
    initialAppliancesRef.current = initialAppliances
  }
  // We need to set the initial appliances ref to the initial appliances so that the form is initialized with the correct appliances
  // and not overwritten when the onboarding cookie gets cleared in the useEffect
  const fromOnboardingCookie = onboardingCookie ? true : false

  const handleFormReady = useCallback(
    (instance: Webform) => {
      setFormInstance(instance)

      // Initialize the form with the initial appliances and email
      instance.on('initialized', () => {
        const appliances = initialAppliancesRef.current || {}
        const email = customer?.emailAddress?.emailAddress ?? ''
        instance.submission = {
          data: {
            ...appliances,
            email,
          },
        }
      })

      instance.on('change', (change: { data: FORMIO_FIELDS }) => {
        // If 'other' has been checked - focus on the 'otherInput'
        setProductForm((prev) => ({
          ...prev,
          appliances: change.data as Record<string, boolean | string>,
        }))

        const applianceFormIsValid = getApplianceFormIsValid(change.data)
        setApplianceFormIsValid(applianceFormIsValid)
      })
    },
    [setProductForm, initialAppliancesRef, customer?.emailAddress?.emailAddress],
  )

  const ApplianceForm = useMemo(() => <FormioWrapper src={formUrl} onFormReady={handleFormReady} showSuccess={false} options={{ noAlerts: true }} />, [handleFormReady, formUrl])

  useEffect(() => {
    if (fromOnboardingCookie) {
      void clearOnboardingCookie()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isBusinessForm = useCallback((data: OnBoardingFormDataType): data is Extract<OnBoardingFormDataType, { isBusiness: true }> => {
    return data.isBusiness
  }, [])

  const clearOnboardingFormError = useCallback((field: keyof FormErrors) => {
    setOnboardingFormErrors((prev) => ({ ...prev, [field]: undefined }))
  }, [])

  const clearAllOnboardingFormErrors = useCallback(() => {
    setOnboardingFormErrors({})
  }, [])

  const isOnboardingFormPending = useMemo(() => {
    return mutations.updateCustomer.isPending || mutations.updateCompanyLocation.isPending || mutations.updateCompanyLocationAddress.isPending || mutations.updateB2BInfo.isPending
  }, [mutations.updateCustomer.isPending, mutations.updateCompanyLocation.isPending, mutations.updateCompanyLocationAddress.isPending, mutations.updateB2BInfo.isPending])

  const isOnboardingFormError = useMemo(() => {
    return mutations.updateCustomer.isError || mutations.updateCompanyLocation.isError || mutations.updateCompanyLocationAddress.isError || mutations.updateB2BInfo.isError
  }, [mutations.updateCustomer.isError, mutations.updateCompanyLocation.isError, mutations.updateCompanyLocationAddress.isError, mutations.updateB2BInfo.isError])

  const isOnboardingFormButtonDisabled = useMemo(() => {
    if (isBusinessForm(onboardingFormData)) {
      return (
        !onboardingFormData.businessName ||
        !onboardingFormData.industry ||
        !onboardingFormData.ABN ||
        (onboardingFormData?.ABN?.length && onboardingFormData?.ABN?.length !== 11) ||
        !!onboardingFormErrors.ABN
      )
    }

    return mutations.updateCustomer.isPending || mutations.updateCompanyLocation.isPending || mutations.updateCompanyLocationAddress.isPending || mutations.updateB2BInfo.isPending
  }, [
    onboardingFormData,
    onboardingFormErrors,
    mutations.updateCustomer.isPending,
    mutations.updateCompanyLocation.isPending,
    mutations.updateCompanyLocationAddress.isPending,
    mutations.updateB2BInfo.isPending,
    isBusinessForm,
  ])

  const openAtStep = useCallback((step: number) => {
    setCurrentStep(step)
    setIsOpen(true)
  }, [])

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1)
  }, [])

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(1, prev - 1))
  }, [])

  const submitApplianceForm = useCallback(async () => {
    const formData = formInstance?.data as FORMIO_FIELDS
    const notInteractedWithForm = formData?.hotWater === false && formData?.cooking === false && formData?.heating === false && formData?.other === false

    // We only want to submit the form if the user has interacted with the appliance form to prevent empty submissions
    if (notInteractedWithForm) {
      return
    }

    formInstance?.submit()
  }, [formInstance])

  const validateApplianceForm = useCallback((): ApplianceFormValidationResult => {
    if (!applianceFormIsValid.isValid && applianceFormIsValid.error === ApplianceFormValidationErrors.NO_APPLIANCE_SELECTED) {
      return { isValid: false, error: ApplianceFormValidationErrors.NO_APPLIANCE_SELECTED }
    }

    if (!applianceFormIsValid.isValid && applianceFormIsValid.error === ApplianceFormValidationErrors.OTHER_INPUT_INVALID) {
      formInstance?.submit()
      formInstance?.getComponent('otherInput')?.focus() // Focusing on the otherInput field to show the error

      return { isValid: false, error: ApplianceFormValidationErrors.OTHER_INPUT_INVALID }
    }

    return { isValid: true, error: null }
  }, [formInstance, applianceFormIsValid])

  const handleOnboardingFormSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      clearAllOnboardingFormErrors()

      const defaultLocation = customer?.companyContacts?.nodes?.[0]?.company?.locations?.nodes?.[0]
      const result = formSchema.safeParse(onboardingFormData)

      if (!result.success) {
        const flattenedErrors = z.flattenError(result.error)
        setOnboardingFormErrors(flattenedErrors.fieldErrors)

        return
      }

      if (!defaultLocation) {
        // eslint-disable-next-line no-console
        console.error('No default location found, check Shopify Customer has a company and location')

        return
      }

      if (isBusinessForm(onboardingFormData)) {
        const customerUpdate = await mutations.updateCustomer.mutateAsync({
          firstName: onboardingFormData.firstName,
          lastName: onboardingFormData.lastName,
          phone: onboardingFormData.phone,
          addresses: [
            {
              address1: onboardingFormData.address.address1,
              city: onboardingFormData.address.city,
              countryCode: onboardingFormData.address.countryCode,
              zip: onboardingFormData.address.zip,
              provinceCode: onboardingFormData.address.zoneCode,
            },
          ],
          metafields: [{ namespace: 'custom', key: 'account_state', type: 'single_line_text_field', value: 'B2B Pending' }],
        })

        if (customerUpdate?.error?.inputErrors) {
          customerUpdate.error.inputErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.message as keyof FormErrors]: [error.message] }))
          })

          return
        }

        if (customerUpdate?.error?.userErrors) {
          customerUpdate.error.userErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.field?.[0] as keyof FormErrors]: [error.message] }))
          })

          return
        }

        // B2B account: update customer and add tags
        const results = await Promise.all([
          mutations.updateB2BInfo.mutateAsync({
            email: customer?.emailAddress?.emailAddress ?? '',
            firstName: onboardingFormData.firstName,
            lastName: onboardingFormData.lastName,
            phone: onboardingFormData.phone,
            addresses: [
              onboardingFormData.address
                ? { ...onboardingFormData.address, phone: onboardingFormData.phone }
                : { address1: '', city: '', countryCode: 'AU' as CountryCode, zip: '' },
            ],
            businessName: onboardingFormData.businessName ?? '',
            abn: onboardingFormData.ABN ?? '',
            industry: onboardingFormData.industry ?? '',
          }),
          mutations.addTags.mutateAsync(['Business Customer']), // This tag triggers email notification to Supagas to process this customer
        ])

        if ((results as MutationResult[]).some((result) => result?.error?.inputErrors || result?.error?.userErrors)) {
          const allInputErrors: InputError[] = (results as MutationResult[]).flatMap((r) => r.error?.inputErrors ?? [])
          const allUserErrors: UserError[] = (results as MutationResult[]).flatMap((r) => r.error?.userErrors ?? [])

          allInputErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.message as keyof FormErrors]: [error.message] }))
          })

          allUserErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.field?.[0] as keyof FormErrors]: [error.message] }))
          })

          return
        }

        setTimeout(() => {
          setCustomerState('B2B_ONBOARDING')

          setIsOpen(false)
        }, 3000)

        return
      }

      // Personal account: update customer
      try {
        // Updating the customer account first to ensure that the company/location isn't updated if there are some issues with the customer account
        const customerUpdateRes = await mutations.updateCustomer.mutateAsync({
          firstName: onboardingFormData.firstName,
          lastName: onboardingFormData.lastName,
          phone: onboardingFormData.phone,
          addresses: [
            {
              address1: onboardingFormData.address.address1,
              city: onboardingFormData.address.city,
              countryCode: onboardingFormData.address.countryCode,
              zip: onboardingFormData.address.zip,
              provinceCode: onboardingFormData.address.zoneCode,
            },
          ],
          metafields: [{ namespace: 'custom', key: 'account_state', type: 'single_line_text_field', value: 'Onboarded' }],
        })

        if (customerUpdateRes?.error?.inputErrors) {
          customerUpdateRes.error.inputErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.message as keyof FormErrors]: [error.message] }))
          })

          return
        }

        if (customerUpdateRes?.error?.userErrors) {
          customerUpdateRes.error.userErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.field?.[0] as keyof FormErrors]: [error.message] }))
          })

          return
        }

        // Only update the company/location if the customer account was updated successfully
        const results = await Promise.all([
          mutations.updateCompany.mutateAsync({
            companyId: customer?.companyId,
            input: {
              name: `${onboardingFormData.firstName} ${onboardingFormData.lastName}`,
            },
          }),
          mutations.updateCompanyLocation.mutateAsync({
            locationId: defaultLocation.id,
            input: {
              name: `${onboardingFormData.address.address1} ${onboardingFormData.address.city} ${onboardingFormData.address.zoneCode}, ${onboardingFormData.address.zip}`,
            },
          }),
          mutations.updateCompanyLocationAddress.mutateAsync({
            locationId: defaultLocation.id,
            address: onboardingFormData.address,
          }),
          mutations.updateMetafields.mutateAsync([{ namespace: 'custom', key: 'location_type', type: 'single_line_text_field', value: 'B2C', ownerId: defaultLocation.id }]),
        ])

        if ((results as MutationResult[]).some((result) => result?.error?.inputErrors || result?.error?.userErrors)) {
          const allInputErrors: InputError[] = (results as MutationResult[]).flatMap((r) => r.error?.inputErrors ?? [])
          const allUserErrors: UserError[] = (results as MutationResult[]).flatMap((r) => r.error?.userErrors ?? [])

          allInputErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.message as keyof FormErrors]: [error.message] }))
          })

          allUserErrors.forEach((error) => {
            setOnboardingFormErrors((prev) => ({ ...prev, [error.field?.[0] as keyof FormErrors]: [error.message] }))
          })

          return
        }

        handleBuyerIdentityChange(defaultLocation.id)

        setCustomerState('ONBOARDED')
        nextStep()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)

        return
      }
    },
    [
      onboardingFormData,
      customer,
      mutations.updateB2BInfo,
      mutations.updateCustomer,
      mutations.updateCompany,
      mutations.updateCompanyLocation,
      mutations.updateCompanyLocationAddress,
      mutations.updateMetafields,
      mutations.addTags,
      clearAllOnboardingFormErrors,
      handleBuyerIdentityChange,
      nextStep,
      isBusinessForm,
    ],
  )

  const value = useMemo(
    () => ({
      onboardingFormData,
      fromOnboardingCookie,
      onboardingFormErrors,
      handleOnboardingFormChange,
      setOnboardingFormErrors,
      clearOnboardingFormError,
      clearAllOnboardingFormErrors,
      isOnboardingFormPending,
      isOnboardingFormError,
      isOnboardingFormButtonDisabled,
      openAtStep,
      nextStep,
      previousStep,
      setOnboardingFormData,
      handleOnboardingFormSubmit,
      displayAddress,
      setDisplayAddress,
      customerState,
      isOpen,
      setIsOpen,
      currentStep,
      setCurrentStep,
      productForm,
      setProductForm,
      initialAppliances,
      isBusinessForm,
      ApplianceForm,
      submitApplianceForm,
      validateApplianceForm,
    }),
    [
      onboardingFormData,
      fromOnboardingCookie,
      onboardingFormErrors,
      handleOnboardingFormChange,
      setOnboardingFormErrors,
      clearOnboardingFormError,
      clearAllOnboardingFormErrors,
      isOnboardingFormPending,
      isOnboardingFormError,
      isOnboardingFormButtonDisabled,
      openAtStep,
      nextStep,
      previousStep,
      setOnboardingFormData,
      handleOnboardingFormSubmit,
      displayAddress,
      setDisplayAddress,
      customerState,
      initialAppliances,
      isOpen,
      setIsOpen,
      currentStep,
      setCurrentStep,
      productForm,
      setProductForm,
      isBusinessForm,
      ApplianceForm,
      submitApplianceForm,
      validateApplianceForm,
    ],
  )

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}
